import { useAuth, useUser } from '@clerk/clerk-react';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../api/client';
import { consumePendingInvitationToken } from '../../hooks/useInvitations';

interface AuthSyncProps {
  children: React.ReactNode;
}

/**
 * Component that syncs user data with the API on first login.
 * Also handles auto-accepting pending invitations after signup.
 * Should wrap the main app content to ensure sync happens on auth.
 */
export function AuthSync({ children }: AuthSyncProps) {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const { user } = useUser();
  const navigate = useNavigate();
  const syncAttemptedRef = useRef(false);
  const lastUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    const syncUser = async () => {
      // Only sync when signed in and user data is available
      if (!isLoaded || !isSignedIn || !user) {
        syncAttemptedRef.current = false;
        lastUserIdRef.current = null;
        return;
      }

      // Prevent duplicate sync attempts for the same user
      if (syncAttemptedRef.current && lastUserIdRef.current === user.id) {
        return;
      }

      syncAttemptedRef.current = true;
      lastUserIdRef.current = user.id;

      try {
        const token = await getToken();
        if (!token) {
          console.error('AuthSync: Failed to get auth token');
          return;
        }

        // Sync user with the API
        await apiClient.post(
          '/api/v1/auth/sync',
          {
            email: user.primaryEmailAddress?.emailAddress,
            firstName: user.firstName,
            lastName: user.lastName,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        console.log('AuthSync: User synced successfully');

        // Check for pending invitation token and auto-accept
        const pendingInvitationToken = consumePendingInvitationToken();
        if (pendingInvitationToken) {
          console.log('AuthSync: Found pending invitation, attempting to accept...');
          try {
            await apiClient.post(
              `/api/v1/invitations/${pendingInvitationToken}/accept`,
              {},
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            );
            console.log('AuthSync: Invitation accepted successfully');
            // Navigate to projects page after accepting
            navigate('/projects');
          } catch (inviteError) {
            // Log error but don't block - invitation might be expired or already accepted
            console.error('AuthSync: Failed to accept invitation', inviteError);
          }
        }
      } catch (error) {
        // Log error but don't block the user - sync can retry on next load
        console.error('AuthSync: Failed to sync user', error);
      }
    };

    syncUser();
  }, [isLoaded, isSignedIn, user, getToken, navigate]);

  return <>{children}</>;
}
